import AgTechTourHelpButton from "@agtech/web/components/App/Tour/AgTechTourHelpButton";
import { InvoiceChangeInvoiceModifiersDialogTourConfig } from "./InvoiceChangeInvoiceModifiersDialogTourConfig";
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from "@agtech/web/components/Buttons/AgTechButtons";
import { AgTechFormSection } from "@agtech/web/components/Forms/Layout/AgTechFormSection";
import { buildGridEntitySelectionIndex, markGridSelectionEntityAsSelected, markGridSelectionEntityAsUnselected } from "@agtech/web/components/Grids/AgTechGridEntities";
import { useInvoiceModifierDataWriteInvoiceModifierInvoiceChangesAction, ChangeItemOnInvoiceDTO } from "@sales/data/lots/invoices/LotInvoiceDataActions";
import InvoiceChangeInvoiceModifiersGrid from "../../components/grids/InvoiceChangeInvoiceModifiersGrid";
import AgTechFormDialog from "@agtech/web/components/Portals/Dialogs/AgTechFormDialog";
import { useAgTechQuery, useAgTechQueryResultCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { InvoiceModifierDataGetAllInvoiceModifiersQuery } from "@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierDataQueries";
import AgTechFormBody from "@agtech/web/components/Forms/Layout/AgTechFormBody";
import { AgTechFormRow } from "@agtech/web/components/Forms/Layout/AgTechFormRow";
import { AgTechFormReadOnlyTextControl } from "@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl";
import { AgTechMoneyFormatter } from "@agtech/web/utilities/AgTechUtilities";
import SaleContactDetailPopUp from "../../../contacts/popups/detail/SaleContactDetailPopUp";
import { useInvoiceDataGetInvoiceDetailsQuery } from "@sales/data/invoicing/InvoicingDataQueries";
import { calculateInvoiceModifierTotal } from "@sales/data/invoicing/InvoicingData";
import { getContactFullName } from "@agtech/data/contacts/ContactData";
import ContactDialog from "@agtech/web/integrations/contacts/dialogs/ContactDialog";

export type InvoiceChangeInvoiceModifiersDialogProps = {
    invoiceId: number,
    contactId: number
}

const InvoiceChangeInvoiceModifiersDialog = (props: InvoiceChangeInvoiceModifiersDialogProps) => {
    const dialogQuery = useInvoiceChangeInvoiceModifiersDialogQuery({
        invoiceId: props.invoiceId
    });

    const dialogAction = useInvoiceModifierDataWriteInvoiceModifierInvoiceChangesAction(props);

    return (
        <AgTechFormDialog
            width={600}
            dialogData={{
                query: dialogQuery,
                getDialogData: d => {
                    const allSaleInvoiceModifiers = Object.values(d.allInvoiceModifiers);
                    const invoiceInvoiceModifiers = d.invoiceDetails.invoice.details.InvoiceModifiersGiven;

                    return buildGridEntitySelectionIndex({
                        entities: allSaleInvoiceModifiers,
                        isSelected: l => invoiceInvoiceModifiers.find((elem) => elem.id === l.id) !== undefined
                    });
                },
                getDialogContext: dialogData => {
                    return {
                        invoice: dialogData.invoiceDetails.invoice,
                        contact: dialogData.invoiceDetails.contact,
                        appliedInvoiceModifiers: dialogData.invoiceDetails.invoice.details.InvoiceModifiersGiven,
                        allInvoiceModifiers: dialogData.allInvoiceModifiers
                    }
                }
            }} 
            dialogAction={{
                action: dialogAction,
                getActionData: (d, context) => {
                    const InvoiceModifierSelections = Object.values(d);
                    const updatedInvoiceModifiers = InvoiceModifierSelections.filter(d => d.isSelected != d.wasOriginallySelected);

                    return {
                        changes: updatedInvoiceModifiers.map<ChangeItemOnInvoiceDTO>(l => ({
                            remove: l.wasOriginallySelected == true && l.isSelected == false,
                            id: l.entity.id
                        })),
                        invoiceTimestamp: context.invoice.timestamp
                    };
                }
            }}
            structure={(data) => ({
                header: {
                    header: `Update charges and discounts - Invoice #${data.context.invoice.info.number}`,
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={InvoiceChangeInvoiceModifiersDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: 'Submit',
            })}
         >
            {(formContext) => {
                const selectedInvoiceModifiers = Object.values(formContext.entity).filter(d => d.isSelected);

                const lotTotal = formContext.context.invoice.details.lotsPurchased.reduce((acc, curr) => acc + (curr.purchasePrice ?? 0), 0);
                const creditsTotal = formContext.context.invoice.details.creditsApplied.reduce((acc, curr) => acc + (curr.creditAmount ?? 0), 0);
                const updatedInvoiceModifiersTotal = calculateInvoiceModifierTotal(lotTotal, selectedInvoiceModifiers.map(d => d.entity));
            
                const invoiceTotal = lotTotal - updatedInvoiceModifiersTotal - creditsTotal;

                return (
                    <AgTechFormBody>
                        <AgTechFormRow>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption="Contact Name"
                                value={e => getContactFullName(formContext.context.contact.info)}
                                onClick={e => ContactDialog.Open(e, {
                                    contactId: formContext.context.contact.id
                                })}
                                showPopUpOnHover={e => ({
                                    popup: () => <SaleContactDetailPopUp contactId={formContext.context.contact.id} />
                                })}
                            />
                        </AgTechFormRow>
                        <div id='invoice-change-modifiers-summary'>
                        <AgTechFormRow>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Lots sold'
                                value={e => AgTechMoneyFormatter.format(lotTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>-</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Discounts Given'
                                value={e => AgTechMoneyFormatter.format(updatedInvoiceModifiersTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>-</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Credits Applied'
                                value={e => AgTechMoneyFormatter.format(creditsTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>=</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Invoice Amount'
                                value={e => AgTechMoneyFormatter.format(invoiceTotal)}
                                classes='snug'
                            />
                        </AgTechFormRow>
                        </div>
                        <div id='invoice-change-modifiers-grid'>
                        <AgTechFormSection
                            header="Charges and discounts"
                            classes='flexed'
                            bodyClasses='flexed pt-3'
                        >
                            <InvoiceChangeInvoiceModifiersGrid
                                InvoiceModifierSelections={formContext.entity}
                                onInvoiceModifierSelected={async InvoiceModifier => {
                                    formContext.updateFormEntity(curr => {
                                        return markGridSelectionEntityAsSelected({
                                            selections: curr,
                                            entity: {
                                                ...InvoiceModifier
                                            }
                                        });
                                    });
                                }}
                                onInvoiceModifierDeselected={async InvoiceModifier => {
                                    formContext.updateFormEntity(curr => {
                                        return markGridSelectionEntityAsUnselected({
                                            selections: formContext.entity,
                                            entity: {
                                                ...InvoiceModifier
                                            }
                                        });
                                    });
                                }}
                            />
                        </AgTechFormSection>
                        </div>
                    </AgTechFormBody>
                )
            }}
        </AgTechFormDialog>
    )
}

const useInvoiceChangeInvoiceModifiersDialogQuery = (props: { invoiceId: number }) => {
    const invoiceDetailsQuery = useInvoiceDataGetInvoiceDetailsQuery({
        invoiceId: props.invoiceId
    });

    const InvoiceModifiersQuery = useAgTechQuery({
        query: InvoiceModifierDataGetAllInvoiceModifiersQuery
    });

    return useAgTechQueryResultCombination({
        firstQuery: invoiceDetailsQuery,
        secondQuery: InvoiceModifiersQuery,
        projection: (invoiceDetails, allInvoiceModifiers) => {
            return {
                invoiceDetails,
                allInvoiceModifiers
            }
        }
    });
}

InvoiceChangeInvoiceModifiersDialog.Open = (e: AgTechClickEventHandler, props: InvoiceChangeInvoiceModifiersDialogProps) => e.dialogs.openDialog({
    dialog: () => <InvoiceChangeInvoiceModifiersDialog {...props} />
});

InvoiceChangeInvoiceModifiersDialog.Button = (props: AgTechButtonProps & InvoiceChangeInvoiceModifiersDialogProps) => {
    let buttonProps: AgTechButtonProps = {
        text: 'Change charges and discounts',
        icon: 'bi bi-arrow-left-right',
        ...props,
        action: e => InvoiceChangeInvoiceModifiersDialog.Open(e, props)
    }

    return <AgTechButton {...buttonProps} />
}

export default InvoiceChangeInvoiceModifiersDialog;